/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import Image from 'next/image'
import { useParallax } from 'react-scroll-parallax'
import { ParallaxProvider } from 'react-scroll-parallax'

import useWindowSize from '@/src/shared/hooks/useWindowSize'
import ImageBody from '@/src/shared/images/body.png'
import ImageBox from '@/src/shared/images/box.svg?url'
import ImageBoxMobile from '@/src/shared/images/box-mobile.svg?url'
import ImageFinger from '@/src/shared/images/finger.png'
import ImageFingerSecond from '@/src/shared/images/finger-2.png'
import ImageHandRight from '@/src/shared/images/hand.png'
import ImageHandLeft from '@/src/shared/images/hand-left.png'
import ImageHead from '@/src/shared/images/head.png'
import ImageLeftFingers from '@/src/shared/images/left-fingers.png'
import ImageRightFingers from '@/src/shared/images/right-fingers.png'
import ImageBoxShadow from '@/src/shared/images/shadow.svg?url'
import ImageStoneBlue from '@/src/shared/images/stone-blue.png' // 162 x 170
import ImageStoneGold from '@/src/shared/images/stone-gold.png' // 190 x 170
import ImageStoneGreen from '@/src/shared/images/stone-green.png' // 169 x 172
import ImageStones from '@/src/shared/images/stones.png' // 338 x 170
import ImageStonesSand from '@/src/shared/images/stones-sand.png' // 338 x 170
import { MainButton } from '@/src/widgets/Mummy/ui/MainButton'

const MummyContent = ({ event }: any) => {
  const { currentSize } = useWindowSize()
  const isMobileVersion = currentSize < 767

  const { ref: headRef } = useParallax<HTMLImageElement>({
    translateY: [0, -10],
    translateX: ['-50%', '-50%'],
    rotate: [0, -6],
    startScroll: 0,
    endScroll: 100,
  })

  const { ref: leftFingersRef } = useParallax<HTMLImageElement>({
    translateY: [0, 0],
    rotate: [0, -5],
    startScroll: 0,
    endScroll: 120,
    speed: 20,
  })

  const { ref: rightFingersRef } = useParallax<HTMLImageElement>({
    translateY: [0, 0],
    rotate: [0, 5],
    startScroll: 0,
    endScroll: 120,
    speed: 20,
  })

  const { ref: stoneGoldRef } = useParallax<HTMLImageElement>({
    disabled: !isMobileVersion,
    translateY: [0, 0],
    translateX: [0, 15],
    startScroll: 5,
    endScroll: 35,
    speed: 5,
  })

  const { ref: stonesRef } = useParallax<HTMLImageElement>({
    disabled: !isMobileVersion,
    translateY: [0, 0],
    translateX: [0, 35],
    startScroll: 10,
    endScroll: 120,
  })

  const { ref: sandStonesRef } = useParallax<HTMLImageElement>({
    disabled: !isMobileVersion,
    translateY: [0, 0],
    translateX: [0, -33],
    startScroll: 10,
    endScroll: 85,
  })

  const { ref: blueStoneRef } = useParallax<HTMLImageElement>({
    disabled: !isMobileVersion,
    translateY: [0, 0],
    translateX: [0, -80],
    startScroll: 10,
    endScroll: 85,
  })

  const { ref: greenStoneRef } = useParallax<HTMLImageElement>({
    disabled: !isMobileVersion,
    translateY: [0, 0],
    translateX: [0, -69],
    startScroll: 10,
    endScroll: 85,
  })

  return (
    <div className="intro__center">
      <div
        className="intro__form"
        style={{
          background: `url(${ImageBox.src}) no-repeat center/contain, url(${ImageBoxShadow.src}) no-repeat 2px 5px/contain`,
        }}
      >
        <div className="intro__mobile-block">
          <Image
            priority
            width={317}
            height={324}
            src={ImageBoxMobile}
            className="intro__mobile-block__box"
            alt="box mobile"
          />

          <Image
            ref={headRef}
            priority
            width={130}
            height={128}
            src={ImageHead}
            className="intro__mobile-block__head"
            alt="mummy head"
          />
          <Image
            ref={leftFingersRef}
            priority
            width={97}
            height={120}
            src={ImageLeftFingers}
            className="intro__mobile-block__left-fingers"
            alt="left fingers"
          />
          <Image
            ref={rightFingersRef}
            priority
            width={97}
            height={120}
            src={ImageRightFingers}
            className="intro__mobile-block__right-fingers"
            alt="right fingers"
          />
        </div>

        <Image
          priority
          width={232}
          height={213}
          src={ImageHandLeft}
          className="intro__mummy-hand-left"
          alt={'hand-left'}
        />

        <div className="intro__mummy-hand-right">
          <Image
            priority
            width={153}
            height={182}
            src={ImageHandRight}
            className="intro__mummy-hand"
            alt={'hand-right'}
          />
          <Image
            priority
            width={45}
            height={48}
            src={ImageFinger}
            className="intro__mummy-finger"
            alt={'hand-finger'}
          />
          <Image
            priority
            width={43}
            height={31}
            src={ImageFingerSecond}
            className="intro__mummy-finger-second"
            alt={'hand-finger-second'}
          />
        </div>

        <Image
          priority
          width={303}
          height={394}
          src={ImageBody}
          className="intro__mummy-body"
          alt={'mummy-body'}
        />

        <Image
          priority
          width={254}
          height={253}
          src={ImageHead}
          className="intro__mummy-head"
          alt={'mummy-head'}
        />

        <Image
          ref={blueStoneRef}
          priority
          width={162}
          height={170}
          src={ImageStoneBlue}
          className="intro__mummy-stone-blue"
          alt={'mummy-stone-blue'}
        />

        <Image
          ref={stoneGoldRef}
          priority
          width={190}
          height={170}
          src={ImageStoneGold}
          className="intro__mummy-stone-gold"
          alt={'mummy-stone-gold'}
        />

        <Image
          ref={greenStoneRef}
          priority
          width={169}
          height={172}
          src={ImageStoneGreen}
          className="intro__mummy-stone-green"
          alt={'mummy-stone-green'}
        />

        <Image
          ref={sandStonesRef}
          priority
          width={338}
          height={170}
          src={ImageStonesSand}
          className="intro__mummy-stones-sand"
          alt={'mummy-stones-sand'}
        />

        <Image
          ref={stonesRef}
          priority
          width={338}
          height={170}
          src={ImageStones}
          className="intro__mummy-stones"
          alt={'mummy-stones'}
        />

        <div className="intro__form-info">
          <h2 className="intro__form-info-title">{event.title}</h2>

          {event.description && (
            <div className="intro__form-info-description">{event.description}</div>
          )}

          {event.button_text && <MainButton>{event.button_text}</MainButton>}
        </div>
      </div>
    </div>
  )
}

const ProviderWrapper = ({ event }: any) => {
  return (
    <ParallaxProvider>
      <MummyContent event={event} />
    </ParallaxProvider>
  )
}

export default ProviderWrapper
